/**
 * Generates a unique color based on the name
 * @param {string} name - Workspace/user name
 * @returns {string} Hexadecimal color
 */
export const generateAvatarColor = (name) => {
  if (!name) return '#2563eb'; // Default blue color
  
  // Predefined colors for avatars
  const colors = [
    '#2563eb', // Blue
    '#10b981', // Green
    '#ef4444', // Red
    '#f59e0b', // Yellow/Orange
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#f97316', // Orange
    '#6366f1', // Indigo
  ];
  
  // Generate an index based on the name using hash
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Use the absolute value of the hash to select a color from the array
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

/**
 * Generates mock avatar URL using UI Avatars
 * @param {string} name - Workspace/user name
 * @returns {string|null} Avatar URL or null if no name
 */
export const generateMockAvatarUrl = (name) => {
  if (!name) return null;
  
  // Extract initials (maximum 2)
  const words = name.trim().split(/\s+/);
  let initials = '';
  
  if (words.length >= 2) {
    // If there are 2 or more words, take the first letter of the first 2
    initials = (words[0][0] + words[1][0]).toUpperCase();
  } else if (words.length === 1) {
    // If there's only one word, take the first 2 letters
    initials = words[0].substring(0, 2).toUpperCase();
  }
  
  // Generate color for the avatar
  const color = generateAvatarColor(name);
  const backgroundColor = color.replace('#', ''); // Remove # for URL
  
  // Generate UI Avatars URL with initials and color
  const encodedName = encodeURIComponent(initials);
  return `https://ui-avatars.com/api/?name=${encodedName}&size=128&background=${backgroundColor}&color=ffffff&bold=true`;
};

/**
 * Validates if an image URL is valid
 * @param {string} url - URL to validate
 * @returns {boolean} true if URL is valid, false otherwise
 */
export const isValidImageUrl = (url) => {
  if (!url) return false;
  
  // Verify that it's a valid URL and not an invalid mock URL
  try {
    const urlObj = new URL(url);
    // If it's an example URL or invalid mock, return false
    if (urlObj.hostname.includes('example.com') || 
        urlObj.hostname.includes('cdn.example.com') ||
        urlObj.hostname.includes('placeholder')) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Gets the avatar URL to use (real or mock)
 * @param {string|null|undefined} photoUrl - Original photo URL
 * @param {string} name - Workspace/user name to generate mock
 * @returns {string|null} Avatar URL to use
 */
export const getAvatarUrl = (photoUrl, name) => {
  // If URL is valid, use it; otherwise, generate a mock URL
  if (isValidImageUrl(photoUrl)) {
    return photoUrl;
  }
  return generateMockAvatarUrl(name);
};

